import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export interface ScheduledArena {
  id: string;
  name: string;
  topic: string;
  description: string | null;
  scheduled_at: string;
  duration_minutes: number;
  created_by: string;
  max_participants: number;
  is_video_enabled: boolean;
  is_voice_only: boolean;
  language: string;
  arena_type: string;
  is_cancelled: boolean;
  live_arena_id: string | null;
  created_at: string;
  rsvp_count?: number;
  user_rsvp_status?: string | null;
  creator?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export const useScheduledArenas = () => {
  const { user } = useAuthContext();
  const [scheduledArenas, setScheduledArenas] = useState<ScheduledArena[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchScheduledArenas = useCallback(async () => {
    setLoading(true);
    
    const { data, error } = await supabase
      .from("scheduled_arenas")
      .select(`
        *,
        profiles:created_by (id, name, photo_url),
        arena_rsvps (id, user_id, rsvp_status)
      `)
      .gte("scheduled_at", new Date().toISOString())
      .order("scheduled_at", { ascending: true });

    if (!error && data) {
      const arenasWithCounts = data.map((arena: any) => {
        const rsvps = arena.arena_rsvps || [];
        const userRsvp = user ? rsvps.find((r: any) => r.user_id === user.id) : null;
        
        return {
          ...arena,
          creator: arena.profiles,
          rsvp_count: rsvps.filter((r: any) => r.rsvp_status === "going").length,
          user_rsvp_status: userRsvp?.rsvp_status || null,
        };
      });
      setScheduledArenas(arenasWithCounts);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchScheduledArenas();
  }, [fetchScheduledArenas]);

  // Create scheduled arena
  const createScheduledArena = useCallback(async (arena: {
    name: string;
    topic: string;
    description?: string;
    scheduled_at: string;
    duration_minutes?: number;
    max_participants?: number;
    is_video_enabled?: boolean;
    is_voice_only?: boolean;
    language?: string;
    arena_type?: string;
  }) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { data, error } = await supabase
      .from("scheduled_arenas")
      .insert({
        ...arena,
        created_by: user.id,
      })
      .select()
      .single();

    if (!error) {
      fetchScheduledArenas();
    }
    return { data, error };
  }, [user, fetchScheduledArenas]);

  // RSVP to arena
  const rsvpToArena = useCallback(async (arenaId: string, status: "going" | "maybe" | "not_going") => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("arena_rsvps")
      .upsert({
        scheduled_arena_id: arenaId,
        user_id: user.id,
        rsvp_status: status,
      }, {
        onConflict: "scheduled_arena_id,user_id",
      });

    if (!error) {
      setScheduledArenas(prev => prev.map(a => 
        a.id === arenaId 
          ? { 
              ...a, 
              user_rsvp_status: status,
              rsvp_count: status === "going" 
                ? (a.user_rsvp_status !== "going" ? (a.rsvp_count || 0) + 1 : a.rsvp_count)
                : (a.user_rsvp_status === "going" ? Math.max(0, (a.rsvp_count || 0) - 1) : a.rsvp_count)
            }
          : a
      ));
    }
    return { error };
  }, [user]);

  // Cancel RSVP
  const cancelRsvp = useCallback(async (arenaId: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("arena_rsvps")
      .delete()
      .eq("scheduled_arena_id", arenaId)
      .eq("user_id", user.id);

    if (!error) {
      setScheduledArenas(prev => prev.map(a => 
        a.id === arenaId 
          ? { 
              ...a, 
              user_rsvp_status: null,
              rsvp_count: a.user_rsvp_status === "going" ? Math.max(0, (a.rsvp_count || 0) - 1) : a.rsvp_count
            }
          : a
      ));
    }
    return { error };
  }, [user]);

  // Start scheduled arena now (host only)
  const startScheduledArena = useCallback(async (scheduledArenaId: string): Promise<{ arenaId: string | null; error: Error | null }> => {
    if (!user) return { arenaId: null, error: new Error("Not authenticated") };

    // Check if user is the host
    const arena = scheduledArenas.find(a => a.id === scheduledArenaId);
    if (!arena || arena.created_by !== user.id) {
      return { arenaId: null, error: new Error("Only the host can start this arena") };
    }

    // Call the database function to start the arena
    const { data, error } = await supabase
      .rpc('start_scheduled_arena', { scheduled_arena_id: scheduledArenaId });

    if (error) {
      return { arenaId: null, error };
    }

    // Refresh the list
    fetchScheduledArenas();
    
    return { arenaId: data as string, error: null };
  }, [user, scheduledArenas, fetchScheduledArenas]);

  // Cancel scheduled arena (host only)
  const cancelScheduledArena = useCallback(async (scheduledArenaId: string) => {
    if (!user) return { error: new Error("Not authenticated") };

    const arena = scheduledArenas.find(a => a.id === scheduledArenaId);
    if (!arena || arena.created_by !== user.id) {
      return { error: new Error("Only the host can cancel this arena") };
    }

    const { error } = await supabase
      .from("scheduled_arenas")
      .update({ is_cancelled: true })
      .eq("id", scheduledArenaId);

    if (!error) {
      setScheduledArenas(prev => prev.filter(a => a.id !== scheduledArenaId));
    }
    return { error };
  }, [user, scheduledArenas]);

  return {
    scheduledArenas,
    loading,
    createScheduledArena,
    rsvpToArena,
    cancelRsvp,
    startScheduledArena,
    cancelScheduledArena,
    refetch: fetchScheduledArenas,
  };
};
